import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck, Sparkle, ArrowLeft, Trash, CircleNotch, RocketLaunch, CheckCircle, Info,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { UsersThree } from '@phosphor-icons/react';
import { schedulingAPI } from '../api/api';
import { PhaseCard } from '../components/scheduling/PhaseCard';
import { CrewCalendar } from '../components/scheduling/CrewCalendar';

const JOB_TYPES = ['Kitchen Remodel', 'Bathroom Remodel', 'Home Addition', 'Roofing', 'Deck / Patio', 'Flooring', 'Painting', 'Plumbing', 'Electrical', 'Landscaping', 'New Construction', 'Other'];

const STATUS_STYLES = {
  draft: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300',
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
};

export default function AIScheduler() {
  const navigate = useNavigate();
  const userType = localStorage.getItem('userType');
  const [form, setForm] = useState({
    title: '', description: '', job_type: '', location: '',
    start_date: new Date(Date.now() + 86400000 * 3).toISOString().slice(0, 10), crew_size: 3,
  });
  const [generating, setGenerating] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [view, setView] = useState('builder');

  useEffect(() => {
    if (userType !== 'contractor') { navigate('/login'); return; }
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadList = () => schedulingAPI.list().then(({ data }) => setSchedules(data.schedules || [])).catch(() => {});

  const refreshSchedule = useCallback(() => {
    if (!schedule) return;
    schedulingAPI.get(schedule.id).then(({ data }) => setSchedule(data.schedule)).catch(() => {});
    loadList();
  }, [schedule]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const generate = async () => {
    if (form.title.trim().length < 3) return toast.error('Give the job a title');
    if (form.description.trim().length < 10) return toast.error('Describe the job in more detail');
    setGenerating(true);
    try {
      const { data } = await schedulingAPI.generate({ ...form, crew_size: Number(form.crew_size) });
      setSchedule(data.schedule);
      loadList();
      toast.success('Schedule generated!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed — try again');
    } finally {
      setGenerating(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await schedulingAPI.setStatus(schedule.id, status);
      setSchedule((s) => ({ ...s, status }));
      loadList();
      toast.success(status === 'active' ? 'Schedule activated — crew stays notified' : `Marked ${status}`);
    } catch {
      toast.error('Could not update status');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await schedulingAPI.remove(id);
      if (schedule?.id === id) setSchedule(null);
      loadList();
      toast.success('Schedule deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7FC] dark:bg-neutral-900" data-testid="ai-scheduler-page">
      {/* Header */}
      <div className="bg-[#EA580C] border-b-4 border-[#0F172A]">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <button onClick={() => navigate('/contractor-dashboard')} className="flex items-center gap-1 text-white/80 hover:text-white text-sm font-bold mb-2" data-testid="scheduler-back-btn">
            <ArrowLeft size={15} weight="bold" /> Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl border-2 border-white/40 flex items-center justify-center">
              <CalendarCheck size={26} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">AI Scheduling Engine</h1>
              <p className="text-orange-100 text-sm">Describe the job → get a phase-by-phase plan → book the right crew</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button onClick={() => setView('builder')}
              className={`h-9 px-4 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors ${view === 'builder' ? 'bg-white text-[#EA580C]' : 'bg-white/15 text-white hover:bg-white/25'}`}
              data-testid="view-builder-tab">
              <Sparkle size={14} weight="fill" /> Scheduler
            </button>
            <button onClick={() => setView('calendar')}
              className={`h-9 px-4 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors ${view === 'calendar' ? 'bg-white text-[#EA580C]' : 'bg-white/15 text-white hover:bg-white/25'}`}
              data-testid="view-calendar-tab">
              <UsersThree size={14} weight="fill" /> Crew Calendar
            </button>
          </div>
        </div>
      </div>

      {view === 'calendar' && (
        <div className="max-w-6xl mx-auto px-4 py-6">
          <CrewCalendar />
        </div>
      )}

      <div className={view === 'calendar' ? 'hidden' : 'max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-5 gap-6'}>
        {/* Left: form + saved list */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#EA580C] p-5" data-testid="scheduler-form">
            <h2 className="font-bold text-lg text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-[#EA580C]" /> New Schedule
            </h2>
            <div className="space-y-3">
              <input value={form.title} onChange={set('title')} placeholder="Job title — e.g. Full kitchen remodel, 123 Oak St"
                className="w-full h-11 px-3.5 rounded-xl border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm font-medium focus:border-[#EA580C] outline-none dark:text-white" data-testid="schedule-title-input" />
              <textarea value={form.description} onChange={set('description')} rows={5}
                placeholder="Describe the scope: what's being built or renovated, size, materials, anything unusual..."
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm focus:border-[#EA580C] outline-none dark:text-white resize-none" data-testid="schedule-description-input" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.job_type} onChange={set('job_type')} className="h-11 px-3 rounded-xl border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm font-medium dark:text-white" data-testid="schedule-jobtype-select">
                  <option value="">Job type...</option>
                  {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input value={form.location} onChange={set('location')} placeholder="City (optional)"
                  className="h-11 px-3.5 rounded-xl border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm font-medium dark:text-white" data-testid="schedule-location-input" />
                <div>
                  <label className="text-[11px] font-bold uppercase text-neutral-400">Start date</label>
                  <input type="date" value={form.start_date} onChange={set('start_date')}
                    className="w-full h-11 px-3 rounded-xl border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm font-medium dark:text-white" data-testid="schedule-startdate-input" />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase text-neutral-400">Crew size</label>
                  <select value={form.crew_size} onChange={set('crew_size')} className="w-full h-11 px-3 rounded-xl border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm font-medium dark:text-white" data-testid="schedule-crewsize-select">
                    {[1, 2, 3, 4, 5, 6, 8, 10, 15, 20].map((n) => <option key={n} value={n}>{n} worker{n > 1 ? 's' : ''}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={generate} disabled={generating}
                className="w-full h-12 rounded-full bg-[#EA580C] hover:bg-[#D95800] disabled:opacity-60 text-white font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]" data-testid="generate-schedule-btn">
                {generating ? (<><CircleNotch size={19} className="animate-spin" /> Building your schedule...</>) : (<><Sparkle size={18} weight="fill" /> Generate Schedule</>)}
              </button>
            </div>
          </div>

          {schedules.length > 0 && (
            <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#EA580C] p-5" data-testid="saved-schedules">
              <h2 className="font-bold text-sm uppercase tracking-wide text-neutral-400 mb-3">My Schedules</h2>
              <div className="space-y-2">
                {schedules.map((s) => (
                  <div key={s.id} className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 cursor-pointer transition-colors ${schedule?.id === s.id ? 'border-[#EA580C] bg-orange-50 dark:bg-orange-900/20' : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-400'}`}
                    onClick={() => setSchedule(s)} data-testid={`schedule-list-item-${s.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-neutral-900 dark:text-white truncate">{s.title}</p>
                      <p className="text-xs text-neutral-500">{s.start_date} → {s.end_date} · {s.phases.length} phases</p>
                    </div>
                    <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${STATUS_STYLES[s.status]}`}>{s.status}</span>
                    <button onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="w-7 h-7 rounded-full hover:bg-red-50 dark:hover:bg-red-900/30 text-neutral-400 hover:text-red-500 flex items-center justify-center" data-testid={`delete-schedule-${s.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: schedule detail */}
        <div className="lg:col-span-3">
          {!schedule ? (
            <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-dashed border-neutral-300 dark:border-neutral-600 p-12 text-center min-h-[380px] flex flex-col items-center justify-center" data-testid="scheduler-empty-state">
              <CalendarCheck size={52} weight="duotone" className="text-neutral-300 dark:text-neutral-600 mb-3" />
              <h3 className="font-bold text-lg text-neutral-700 dark:text-neutral-200">No schedule selected</h3>
              <p className="text-sm text-neutral-500 max-w-sm mt-1">Generate a new schedule or pick one from your list. The AI breaks the job into phases with dates, then suggests the best available crew for each one.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="schedule-detail">
              <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#EA580C] p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-xl font-bold text-neutral-900 dark:text-white">{schedule.title}</h2>
                    <p className="text-sm text-neutral-500 mt-0.5">
                      {schedule.start_date} → {schedule.end_date} · {schedule.phases.length} phases · crew of {schedule.crew_size}
                      {schedule.location ? ` · ${schedule.location}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-bold uppercase rounded-full px-2.5 py-1 ${STATUS_STYLES[schedule.status]}`} data-testid="schedule-status-badge">{schedule.status}</span>
                    {schedule.status === 'draft' && (
                      <button onClick={() => setStatus('active')} className="h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5" data-testid="activate-schedule-btn">
                        <RocketLaunch size={14} weight="fill" /> Activate
                      </button>
                    )}
                    {schedule.status === 'active' && (
                      <button onClick={() => setStatus('completed')} className="h-9 px-4 rounded-full border-2 border-emerald-600 text-emerald-700 dark:text-emerald-300 text-xs font-bold flex items-center gap-1.5" data-testid="complete-schedule-btn">
                        <CheckCircle size={14} weight="fill" /> Mark Complete
                      </button>
                    )}
                  </div>
                </div>
                {schedule.ai_notes && (
                  <div className="mt-3 flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl px-3.5 py-2.5 text-sm text-blue-900 dark:text-blue-200" data-testid="schedule-ai-notes">
                    <Info size={16} weight="fill" className="shrink-0 mt-0.5 text-blue-500" /> {schedule.ai_notes}
                  </div>
                )}
              </div>

              {schedule.phases.map((p, i) => (
                <PhaseCard key={p.phase_id} scheduleId={schedule.id} phase={p} index={i} onChanged={refreshSchedule} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
